import Tracker from './modules/tracker';
import logger from './modules/logger';
import * as events from './modules/events';
import Dispatcher from './modules/dispatcher';
import UIControl from './modules/uicontrol';
import Flow from './modules/flow/flow';
import Integrator from './modules/integrator';
import applyIntegratorMocks from './modules/integrator_mock';

const LandingPage = {
    // initialisation data
    data: {
        translations: {},
        metadata: {},
        integratorBaseUrl: '',
        integratorIdentity: '',
        flowModule: '',
        strategy: '',
        plan: '',
        territory: '',
        productUrl: '',
        productSlug: '',
        locale: 'xx',
        request_id: '',
        identity: '',
        isKnown: null,
        fraudDetection: '',
        trackConversions: false,
        environment: 'prod',
        integratorResponseCode: 200,
        integratorIdentifyUrl: '',
        internalCall: false,
        heAuthToken: '',
        suggestedUsername: '',
        csrfToken: '',
        pageContext: '',
        integratorMock: null,
    },

    // modules
    logger,
    events,
    integrator: {},
    tracker: {},
    dispatcher: {},
    uiControl: {},
    flow: {},

    markBoot(step, detail = {}, level = 'info') {
        const boot = window.__LP_BOOT__ = window.__LP_BOOT__ || { startedAt: Date.now(), marks: [] };
        const payload = {
            ...detail,
            step,
            timestamp: Date.now(),
            elapsed_ms: Date.now() - (boot.startedAt || Date.now()),
            url: window.location.href,
            user_agent: window.navigator.userAgent,
            landing_page_exists: typeof window.LandingPage !== 'undefined',
            flow_module_exists: typeof window.FlowModule !== 'undefined'
        };

        if (typeof window.lpBootMark === 'function') {
            window.lpBootMark(step, detail, level);
        }

        if (level === 'error') {
            this.logger.error(`[LP Boot] ${step}`, payload);
        } else if (level === 'warn') {
            this.logger.warn(`[LP Boot] ${step}`, payload);
        } else {
            this.logger.debug(`[LP Boot] ${step}`, payload);
        }
    },

    /**
     * Initialise the LP object with all the modules needed
     * @param {object} options
     */
    init(options) {
        try {
            if (options && typeof options === 'object') {
                const props = Object.keys(options);
                for (let i = 0; i < props.length; i += 1) {
                    const key = props[i];
                    if (Object.prototype.hasOwnProperty.call(options, key)
                        && Object.prototype.hasOwnProperty.call(this.data, key)) {
                        this.data[key] = options[key];
                    }
                }
            }

            this.logger.setLevel("info");

            this.logger.addGlobalContext({ "event_tracking_id": this.data.metadata.event_tracking_id, "traffic_source": this.data.metadata.ts ?? "" })
            if (options.logContext) {
                this.logger.addGlobalContext(options.logContext);
            }

            // Initialise modules
            this.dispatcher = new Dispatcher(this.logger);
            this.tracker = new Tracker(
                this.logger,
                {
                    pageRequestId: this.data.request_id,
                    data: {
                        locale: this.data.locale,
                        identity: this.data.identity,
                        identified: this.data.isKnown,
                    },
                },
                this.data.metadata.event_tracking_id,
            );
            // Track ShownLandingPage Event
            this.tracker.track(this.events.SHOWN_LANDING_PAGE);
            this.uiControl = new UIControl(this.data.translations, this.dispatcher, this.logger);
            // set up integrator with default/base data
            this.integrator = new Integrator(
                this.data.integratorBaseUrl,
                {
                    plan: this.data.plan,
                    locale: this.data.locale,
                    product: this.data.productSlug,
                    csrfToken: this.data.csrfToken
                },
                this.logger,
                this.data.pageContext,
            );
            applyIntegratorMocks(this.integrator, this.data.integratorMock, this.logger);
            // set up flow
            this.flow = new Flow(this.data.flowModule, this.logger);

            if (!window.FlowModule) {
                this.markBoot('flow_module_invalid', {
                    flow_module_type: typeof window.FlowModule
                }, 'error');
            }

            this.flow.init({
                dispatcher: this.dispatcher,
                tracker: this.tracker,
                uiControl: this.uiControl,
                logger: this.logger,
                integrator: this.integrator,
                productUrl: this.data.productUrl,
                integratorIdentity: this.data.integratorIdentity,
                productSlug: this.data.productSlug,
                locale: this.data.locale,
                identity: this.data.identity,
                plan: this.data.plan,
                territory: this.data.territory,
                trackConversions: this.data.trackConversions,
                internalCall: false,
                fraudDetection: this.data.fraudDetection,
                integratorResponseCode: this.data.integratorResponseCode,
                integratorIdentifyUrl: this.data.integratorIdentifyUrl,
                metadata: this.data.metadata,
                heAuthToken: this.data.heAuthToken,
                suggestedUsername: this.data.suggestedUsername,
            });
            this.logger.debug('LandingPage: finished initialising');
            this.dispatcher.dispatchEvent(events.APP_POST_INIT);

            // Track FlowExit Event
            const flowExitHandler = () => {
                this.dispatcher.dispatchEvent(events.FLOW_EXIT);
            };
            // Track users performance of the landing page
            window.addEventListener('beforeunload', flowExitHandler.bind(this));
            window.addEventListener('unload', flowExitHandler.bind(this));
        } catch (error) {
            this.markBoot('landing_page_init_exception', {
                error_name: error instanceof Error ? error.name : typeof error,
                error_message: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : null
            }, 'error');
            throw error;
        }
    },
    /**
     * Add metadata to the LP object
     * @param {object} meta
     */
    addMetadata(meta) {
        this.data.metadata = Object.assign(this.data.metadata, meta);
    },

    /**
     * Initialise fraud detection scripts
     */
    bootstrapFraudDetection(provider) {
        this.logger.debug('LandingPage: bootstrapping FraudDetection', provider);
        this.data.fraudDetection = provider;
    },

};

export default LandingPage;

window.LandingPage = LandingPage;
